import React, { useState, useMemo } from 'react';

/**
 * Console développeur pour afficher les logs et phases LLM
 */
const DevConsole = ({ consoleLogs = [], selectedStep = null, logDetailLevel = 'standard' }) => {
  const [filter, setFilter] = useState('all'); // all, v9_step1, v9_step2, error, success
  const [searchTerm, setSearchTerm] = useState('');

  const filteredLogs = useMemo(() => {
    let logs = consoleLogs || [];

    // Filter by step
    if (filter !== 'all') {
      logs = logs.filter(log => {
        if (filter === 'error') return log.type === 'error';
        if (filter === 'success') return log.type === 'success';
        if (filter === 'info') return log.type === 'info';
        return log.step === filter;
      });
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      logs = logs.filter(log =>
        log.message?.toLowerCase().includes(term) ||
        log.step?.toLowerCase().includes(term)
      );
    }

    return logs;
  }, [consoleLogs, filter, searchTerm]);

  const getLogIcon = (type) => {
    switch (type) {
      case 'error':
        return '❌';
      case 'success':
        return '✅';
      case 'info':
        return 'ℹ️';
      case 'warning':
        return '⚠️';
      default:
        return '📝';
    }
  };

  const getLogColor = (type) => {
    switch (type) {
      case 'error':
        return 'text-red-400 bg-red-900/20 border-red-500/30';
      case 'success':
        return 'text-green-400 bg-green-900/20 border-green-500/30';
      case 'warning':
        return 'text-yellow-400 bg-yellow-900/20 border-yellow-500/30';
      default:
        return 'text-slate-300 bg-slate-900/60 border-slate-700/60';
    }
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Console Développeur</h2>
          <p className="text-sm text-slate-400">
            {filteredLogs.length} log(s) affiché(s) sur {consoleLogs.length}
          </p>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <option value="all">Tous</option>
            <option value="v9_step1">Step 1</option>
            <option value="v9_step2">Step 2</option>
            <option value="error">Erreurs</option>
            <option value="success">Succès</option>
            <option value="info">Info</option>
          </select>
        </div>
      </div>

      <div className="bg-slate-900 rounded-xl border border-slate-700 max-h-[600px] overflow-y-auto">
        {filteredLogs.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            Aucun log à afficher
          </div>
        ) : (
          <div className="p-4 space-y-2">
            {filteredLogs.map((log, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg border ${getLogColor(log.type)}`}
              >
                <div className="flex items-start gap-3">
                  <span className="text-lg">{getLogIcon(log.type)}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {log.step && (
                        <span className="px-2 py-1 bg-slate-800 rounded text-xs font-mono text-orange-300">
                          {log.step}
                        </span>
                      )}
                      {log.timestamp && (
                        <span className="text-xs text-slate-500">
                          {new Date(log.timestamp).toLocaleTimeString()}
                        </span>
                      )}
                    </div>
                    <p className="text-sm break-words">{log.message}</p>
                    {logDetailLevel !== 'minimal' && log.inputs && (
                      <details className="mt-2">
                        <summary className="text-xs text-slate-400 cursor-pointer">
                          Inputs
                        </summary>
                        <pre className="mt-2 p-2 bg-slate-950 rounded text-xs overflow-x-auto">
                          {JSON.stringify(log.inputs, null, 2)}
                        </pre>
                      </details>
                    )}
                    {logDetailLevel !== 'minimal' && log.outputs && (
                      <details className="mt-2">
                        <summary className="text-xs text-slate-400 cursor-pointer">
                          Outputs
                        </summary>
                        <pre className="mt-2 p-2 bg-slate-950 rounded text-xs overflow-x-auto">
                          {JSON.stringify(log.outputs, null, 2)}
                        </pre>
                      </details>
                    )}
                    {logDetailLevel === 'verbose' && log.params && (
                      <details className="mt-2">
                        <summary className="text-xs text-slate-400 cursor-pointer">
                          Params
                        </summary>
                        <pre className="mt-2 p-2 bg-slate-950 rounded text-xs overflow-x-auto">
                          {JSON.stringify(log.params, null, 2)}
                        </pre>
                      </details>
                    )}
                    {logDetailLevel === 'debug' && log.raw && (
                      <details className="mt-2">
                        <summary className="text-xs text-slate-400 cursor-pointer">
                          Raw Data
                        </summary>
                        <pre className="mt-2 p-2 bg-slate-950 rounded text-xs overflow-x-auto">
                          {JSON.stringify(log.raw, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DevConsole;

